import { useEffect, useRef } from "react";
import { loadPrefs } from "@/lib/storage";
import { getFeaturedToday, listRecent } from "@/lib/api";

const IS_DEV = process.env.NODE_ENV === "development";
const debug = (...args) => { if (IS_DEV) console.debug("[notify]", ...args); };
const LAST_FIRED_KEY = "dnc.notify.lastFired";  // YYYY-MM-DD

/**
 * Client-side daily reminder scheduler.
 *
 * Fires a browser Notification at the user's chosen local time each day.
 * Because we don't run a push server, notifications only fire while the
 * app has a live tab (or PWA window) open. To improve reliability, we:
 *   - Check every minute if it's time.
 *   - Guard against multiple fires per day via LAST_FIRED_KEY.
 *   - Re-read prefs on every tick so live changes take effect without reload.
 *   - Ping the SW so the notification survives if the tab is backgrounded.
 */
function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

async function buildNotificationBody() {
  // Try today's featured brew first — it's the freshest guaranteed capsule.
  // If not ready (or brand-new install with no data), fall back to the
  // reader's most recent capsule headlines.
  const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");
  try {
    const featured = await getFeaturedToday();
    const heads = (featured?.segments || [])
      .map((s) => s.headline)
      .filter(Boolean)
      .slice(0, 2);
    if (heads.length > 0) {
      return `Today's brew · ${heads.map((h) => truncate(h, 55)).join(" · ")}`;
    }
  } catch (err) { debug("featured fetch failed:", err?.message || err); }
  try {
    const recent = await listRecent(1);
    const heads = (recent?.[0]?.headlines || []).slice(0, 2);
    if (heads.length > 0) {
      return `Latest capsule · ${heads.map((h) => truncate(h, 55)).join(" · ")}`;
    }
  } catch (err) { debug("recent fetch failed:", err?.message || err); }
  return "Tap to brew today's 15-minute news capsule.";
}

async function fireNotification() {
  const title = "Your Daily Capsule is ready";
  const body = await buildNotificationBody();
  const opts = {
    body,
    icon: "/icon-192.svg",
    badge: "/favicon.svg",
    tag: "daily-capsule",
    renotify: true,
    data: { url: "/" },
  };
  try {
    // Prefer the service worker path — it can show notifications even when
    // the page is backgrounded. Fall back to the page-level Notification API.
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.ready;
      if (reg && reg.showNotification) {
        await reg.showNotification(title, opts);
        return true;
      }
    }
    if (typeof Notification !== "undefined") {
      // eslint-disable-next-line no-new
      new Notification(title, opts);
      return true;
    }
  } catch (err) { debug("fireNotification failed:", err?.message || err); }
  return false;
}

export default function NotificationScheduler() {
  const timerRef = useRef(null);

  useEffect(() => {
    const tick = async () => {
      const prefs = loadPrefs();
      if (!prefs.notify_enabled) return;
      if (typeof Notification === "undefined") return;
      if (Notification.permission !== "granted") return;
      const [h, m] = (prefs.notify_time || "08:00").split(":").map((x) => parseInt(x, 10));
      const now = new Date();
      // Fire when we've reached or just passed the target minute for today,
      // and we haven't already fired today.
      const targetMinutes = h * 60 + m;
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      const alreadyFired = localStorage.getItem(LAST_FIRED_KEY) === todayKey();
      // Pre-warm the featured capsule ~3 min before the reminder so the
      // headline teaser in the notification body is ready.
      if (!alreadyFired && targetMinutes - nowMinutes >= 0 && targetMinutes - nowMinutes <= 3) {
        try { await getFeaturedToday(); } catch (err) { debug("pre-warm featured failed:", err?.message || err); }
      }
      if (!alreadyFired && nowMinutes >= targetMinutes && nowMinutes - targetMinutes < 5) {
        // Within a 5-minute window; fire once.
        const ok = await fireNotification();
        if (ok) localStorage.setItem(LAST_FIRED_KEY, todayKey());
      }
    };

    // Kick immediately in case the user opened the tab right on their time,
    // then check every 60s.
    tick();
    timerRef.current = setInterval(tick, 60_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  return null;
}
